import getStorageItem from '../helpers/getStorageItem';
import { ITableSettings, TableSettings } from '../types/types/table';

enum SortDirection {
  DESC = 'desc',
  ASC = 'asc',
}

export const addToLocalStorageObject = function <T>(name: string, data: T) {
  const existing = localStorage.getItem(name);
  let existedItem = {} as T;

  try {
    // JSON.parse can throw exception
    existedItem = existing ? JSON.parse(existing) : {};

    existedItem = { ...existedItem, ...data };
  } catch (e) {
    existedItem = { ...data };
  }

  localStorage.setItem(name, JSON.stringify(existedItem));
};

export const setToLocalStorage = <T>(key: string, data: T): T => {
  return localStorage.setItem(key, JSON.stringify(data)) as T;
};

export const addToLocalStorageQueueStrategy = <T>(
  key: string,
  data: T[],
  queueMaxLength = 5,
) => {
  const settings = getStorageItem(key);
  const savedData: T[] = !settings?.['length']
    ? (settings as T[])
    : ([] as T[]);
  // if there is no such field in local storage
  if (!savedData) {
    setToLocalStorage(key, data);
  } else if (savedData.some((item: T) => data.includes(item))) {
    setToLocalStorage(key, [...Array.from(new Set(data.concat(savedData)))]);
  } else if (queueMaxLength >= savedData.length + 1) {
    // there is free places
    setToLocalStorage(key, data.concat(savedData));
  } else {
    // no free places, remove the last (old) item
    setToLocalStorage(key, data.concat(savedData.slice(0, queueMaxLength - 1)));
  }
};

const SORT_DIRECTIONS: string[] = [
  SortDirection.ASC as string,
  SortDirection.DESC as string,
];

export function getSettingsOrDefault(
  settingsKey: string,
  currentLocalStorageSettings: TableSettings,
  defaultSettings: TableSettings,
) {
  let currentSettings = defaultSettings;

  if (currentLocalStorageSettings) {
    const keyDifferences = Object.keys(currentSettings)?.filter(
      (x) => !Object.keys(currentLocalStorageSettings)?.includes(x),
    );
    if (
      currentLocalStorageSettings &&
      (!keyDifferences.length ||
        // temporary solution hotfix. todo it better
        (keyDifferences.length === 1 &&
          keyDifferences[0] === 'sortingComparators'))
    ) {
      const currentSettingKeys =
        currentLocalStorageSettings.columnsOrderIds.concat(
          currentLocalStorageSettings.hidedColumns,
        );
      const defaultSettingKeys = defaultSettings.columnsOrderIds.concat(
        defaultSettings.hidedColumns,
      );

      // Check if correct values in local storage
      if (
        checkCorrectValuesTableSettings(
          currentSettingKeys,
          defaultSettingKeys,
          currentLocalStorageSettings,
        )
      ) {
        currentSettings = currentLocalStorageSettings;
      } else {
        addToLocalStorageObject(settingsKey, defaultSettings);
      }
    }
  }

  return currentSettings;
}

export function checkCorrectValuesTableSettings(
  currentSettingKeys: string[],
  defaultSettingKeys: string[],
  currentLocalStorageSettings: ITableSettings,
) {
  const orderBySubstrings = currentLocalStorageSettings.orderBy.split('.');

  const localSettingsTableColumnsKeysValid =
    currentSettingKeys.filter((columnKey: string) =>
      defaultSettingKeys.includes(columnKey),
    ).length == defaultSettingKeys.length;

  const localSettingsSortDirectionValid = SORT_DIRECTIONS.includes(
    currentLocalStorageSettings.order,
  );

  const localSettingsSortKeyValid = currentSettingKeys.some((item) =>
    orderBySubstrings.some((subString) =>
      item.toLowerCase().includes(subString.toLowerCase()),
    ),
  );

  return (
    localSettingsTableColumnsKeysValid &&
    localSettingsSortDirectionValid &&
    localSettingsSortKeyValid
  );
}
